import React from "react";
import styled from "styled-components";
import Hero from "src/components/Hero/Hero";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import SubTitleSection from "src/components/SubTitleSection/SubTitleSection";
import MarkdownWithRouter from "src/components/MarkdownWithRouter";
import EventFinished from "src/components/EventFinished";
import { includesAnyPhrase } from "src/utils/markdownText";

const resolveLangValue = (value, lang) => {
  if (typeof value === "string") {
    return value.replace("{lang}", lang).replace("{lng}", lang);
  }

  if (Array.isArray(value)) {
    return value.map((item) => resolveLangValue(item, lang));
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        resolveLangValue(nestedValue, lang),
      ])
    );
  }

  return value;
};

const CMS_BLOCK_MAP = {
  "dining-promo.hero": "Hero",
  "dining-promo.title-section": "TitleSection",
  "dining-promo.text-content": "TextContent",
  "dining-promo.small-text-content": "SmallTextContent",
  "dining-promo.image-section": "ImageSection",
  "dining-promo.split-section": "SplitSection",
  "dining-promo.stack-section": "SplitSection",
  "dining-promo.image-row": "SplitSection",
  "dining-promo.choice-pair": "SplitSection",
};

const toImageSection = (image) =>
  image?.src
    ? { type: "ImageSection", props: { src: image.src, alt: image.alt || "" } }
    : null;

const toConnectorSplitProps = (props = {}) => {
  const items = Array.isArray(props.images)
    ? props.images
    : [
        props.leftImage && { src: props.leftImage, alt: props.leftAlt || "" },
        props.rightImage && { src: props.rightImage, alt: props.rightAlt || "" },
      ].filter(Boolean);

  return {
    withBackground: false,
    className: ["with-connector", props.className].filter(Boolean).join(" "),
    leftSections: [toImageSection(items[0])].filter(Boolean),
    rightSections: [toImageSection(items[1])].filter(Boolean),
  };
};

const toSplitProps = (type, props = {}) => {
  if (type === "StackSection" || props.sections) {
    const { sections, ...rest } = props;
    return {
      ...rest,
      leftSections: props.leftSections || sections || [],
      rightSections: props.rightSections || [],
    };
  }
  if (type === "ImageRow" || type === "ChoicePair") {
    return toConnectorSplitProps(props);
  }
  return props;
};

const normalizeSectionShape = (section) => {
  if (!section) {
    return null;
  }

  if (section.type) {
    const type =
      section.type === "StackSection" ||
      section.type === "ImageRow" ||
      section.type === "ChoicePair"
        ? "SplitSection"
        : section.type;
    return {
      type,
      props: toSplitProps(section.type, section.props || {}),
    };
  }

  if (section.__component) {
    const mappedType = CMS_BLOCK_MAP[section.__component];

    if (!mappedType) {
      console.warn(`Dining CMS block "${section.__component}" not found in registry`);
      return null;
    }

    const { __component, id, ...props } = section;
    const legacyType =
      __component === "dining-promo.stack-section"
        ? "StackSection"
        : __component === "dining-promo.image-row" ||
            __component === "dining-promo.choice-pair"
          ? "ImageRow"
          : mappedType;

    return {
      type: mappedType,
      props: toSplitProps(legacyType, props),
    };
  }

  return null;
};

const normalizeSections = (sections = []) =>
  sections
    .map((section) => normalizeSectionShape(section))
    .filter(Boolean);

const buildLegacySections = (promo) => {
  if (!promo?.hero && !promo?.content) {
    return [];
  }

  const description = promo?.content?.description || promo?.content?.lunch?.description;
  const sideImage = promo?.content?.sideImage || promo?.content?.lunch?.image;
  const sections = [];

  if (promo?.hero) {
    sections.push({
      type: "Hero",
      props: {
        bgImage: promo.hero.bgImage,
        image: promo.hero.image,
        description: promo.hero.title,
        descriptionBgColor: promo.hero.descriptionBgColor || "#0000004d",
        darkenBg: 0,
        buttonSettings: null,
      },
    });
  }

  sections.push({
    type: "SplitSection",
    props: {
      withBackground: true,
      leftSections: [
        {
          type: "TitleSection",
          props: {
            as: "h2",
            subTitle: promo?.content?.subtitle,
          },
        },
        ...(description
          ? [
              {
                type: "TextContent",
                props: {
                  content: description,
                  className: "desc",
                },
              },
            ]
          : []),
        ...(promo?.content?.pgPlayersCard
          ? [
              {
                type: "TextContent",
                props: {
                  content: promo.content.pgPlayersCard,
                  routerAware: true,
                },
              },
            ]
          : []),
      ],
      rightSections: sideImage
        ? [
            {
              type: "ImageSection",
              props: {
                src: sideImage,
                alt: promo?.hero?.title || promo?.content?.subtitle,
              },
            },
          ]
        : [],
      rightColumnClassName: "pt-md-4",
    },
  });

  if (promo?.content?.smallText) {
    sections.push({
      type: "SmallTextContent",
      props: {
        content: promo.content.smallText,
      },
    });
  }

  return sections;
};

const DiningHeroSection = ({ ...props }) => <Hero {...props} />;

const withFrenchMark = (value) => {
  if (typeof value !== "string" || !value.endsWith(".MC")) {
    return value;
  }

  return (
    <>
      {value.slice(0, -2)}
      <sup>MC</sup>
    </>
  );
};

const DiningTitleSection = ({
  as = "h2",
  title,
  subTitle,
  borderColor,
  text,
  className = "",
}) => (
  <SubTitleSection
    as={as}
    subTitle={withFrenchMark(subTitle || title)}
    text={text}
    className={["dining-title-section", className].filter(Boolean).join(" ")}
    borderColor={borderColor}
  />
);

const DiningTextContent = ({
  content,
  routerAware = false,
  className = "",
  markdownVariant = "all-strong",
  strongHighlightPhrases = [],
  strongUnderlinePhrases = [],
}) => {
  if (!content) {
    return null;
  }

  if (routerAware) {
    return <MarkdownWithRouter className={className}>{content}</MarkdownWithRouter>;
  }

  if (markdownVariant !== "selective") {
    return <ReactMarkdown className={className}>{content}</ReactMarkdown>;
  }

  return (
    <ReactMarkdown
      className={className}
      components={{
        strong: ({ children, ...props }) => {
          const text = Array.isArray(children) ? children.join("") : String(children || "");
          const shouldHighlight = includesAnyPhrase(text, strongHighlightPhrases);
          const shouldUnderline = includesAnyPhrase(text, strongUnderlinePhrases);
          const strongClassName = [
            shouldHighlight ? "markdown-highlight" : "",
            shouldUnderline ? "markdown-underline" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <strong className={strongClassName || undefined} {...props}>
              {children}
            </strong>
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

const DiningSmallTextContent = ({ content, className = "" }) => {
  if (!content) {
    return null;
  }

  return (
    <container-div>
      <center-content>
        <ReactMarkdown className={["small", className].filter(Boolean).join(" ")}>
          {content}
        </ReactMarkdown>
      </center-content>
    </container-div>
  );
};

const DiningImageSection = ({ src, alt, className }) => {
  if (!src) {
    return null;
  }

  return <ResponsiveImage src={src} alt={alt} className={className} />;
};

const DiningSplitSection = ({
  leftSections = [],
  rightSections = [],
  leftColumnClassName,
  rightColumnClassName,
  withBackground = true,
  backgroundColor,
  className,
  nested = false,
  renderSection,
  sectionKey,
}) => {
  const left = normalizeSections(leftSections);
  const right = normalizeSections(rightSections);
  const hasRight = right.length > 0;
  const isConnector = typeof className === "string" && className.includes("with-connector");
  const isCompact = !withBackground;
  const row = (
    <Row
      className={[
        nested || isConnector || isCompact ? "g-3" : "g-4 mb-5",
        isConnector ? "with-connector" : "",
        nested ? className : "",
      ]
        .filter(Boolean)
        .join(" ")}
      xs={isConnector ? 2 : 1}
      md={hasRight ? 2 : 1}
    >
      <Col className={leftColumnClassName}>
        {left.map((section, index) =>
          renderSection(section, index, `${sectionKey}-left`)
        )}
      </Col>
      {hasRight && (
        <Col className={rightColumnClassName}>
          {right.map((section, index) =>
            renderSection(section, index, `${sectionKey}-right`)
          )}
        </Col>
      )}
    </Row>
  );

  if (nested) {
    return row;
  }

  return (
    <MainContentSection
      className={className}
      $withBackground={withBackground}
      $backgroundColor={backgroundColor}
      $compact={!withBackground}
    >
      <container-div>{row}</container-div>
    </MainContentSection>
  );
};

const COMPONENT_MAP = {
  Hero: DiningHeroSection,
  TitleSection: DiningTitleSection,
  TextContent: DiningTextContent,
  SmallTextContent: DiningSmallTextContent,
  ImageSection: DiningImageSection,
  SplitSection: DiningSplitSection,
};

const DynamicDiningPromo = ({
  promoKey,
  scheduleKeyName,
  unpublishAt,
  highlightColor,
  sections: propSections,
  theme: propTheme,
  promoData,
}) => {
  const { t, i18n } = useTranslation();
  const promo = promoData || (promoKey ? t(`diningPromos.${promoKey}`, { returnObjects: true }) : null);
  const theme = propTheme || promoData?.theme || promo?.theme || {};
  const resolvedHighlightColor =
    highlightColor || theme.highlightColor || theme.markdownHighlightColor || "#d4bd55";
  const resolvedTitleHighlightColor = theme.titleHighlightColor || resolvedHighlightColor;
  const resolvedMarkdownHighlightColor =
    theme.markdownHighlightColor || resolvedHighlightColor;
  const resolvedMarkdownVariant = theme.markdownVariant || "all-strong";
  const rawSections = propSections || promo?.sections || buildLegacySections(promo);
  const sections = normalizeSections(resolveLangValue(rawSections, i18n.language));

  const renderSection = (section, index, parentKey = "root") => {
    const { type, props = {} } = section || {};
    const Component = COMPONENT_MAP[type];

    if (!Component) {
      console.warn(`Dining component type "${type}" not found in registry`);
      return null;
    }

    return (
      <Component
        key={`${parentKey}-${type}-${index}`}
        {...props}
        {...(type === "TitleSection"
          ? { borderColor: resolvedTitleHighlightColor }
          : {})}
        nested={parentKey !== "root"}
        highlightColor={resolvedHighlightColor}
        markdownVariant={resolvedMarkdownVariant}
        renderSection={renderSection}
        sectionKey={`${parentKey}-${type}-${index}`}
      />
    );
  };

  return (
    <PromoPageDiv
      $highlightColor={resolvedHighlightColor}
      $titleHighlightColor={resolvedTitleHighlightColor}
      $markdownHighlightColor={resolvedMarkdownHighlightColor}
      $markdownVariant={resolvedMarkdownVariant}
    >
      {(unpublishAt || scheduleKeyName) && (
        <EventFinished
          eventType="promotion"
          scheduleKeyName={scheduleKeyName}
          unpublishAt={unpublishAt}
        />
      )}
      {sections.map((section, index) => renderSection(section, index))}
    </PromoPageDiv>
  );
};

const PromoPageDiv = styled.div`
  width: 100%;
  height: auto;
  background: var(--lightblack);
  color: var(--white);
  padding-bottom: 1rem;

  sup {
    font-size: 0.45em;
    line-height: 0;
    vertical-align: super;
    font-weight: 700;
  }

  .hero_image {
    padding: 2rem 2rem 4rem;
    opacity: 0.9;

    @media (min-width: 768px) {
      padding: 3rem;
    }
  }

  .dining-title-section {
    h2:after {
      border-color: ${(props) => props.$titleHighlightColor};
    }
  }

  .dining-title-section.centered {
    text-align: center;

    h2:before {
      display: none;
    }

    h2:after {
      margin-left: auto;
      margin-right: auto;
    }
  }

  .step p {
    margin-bottom: 0.2rem;
  }

  .step-answer p {
    margin-bottom: 1.1rem;
  }

  .text-center {
    max-width: 860px;
    margin: 0.25rem auto 0;
    padding: 0 1.25rem;
  }

  .text-center p {
    margin-bottom: 1.35rem;
  }

  .text-center p:last-child {
    margin-bottom: 0;
  }

  ${(props) =>
    props.$markdownVariant === "all-strong"
      ? `.desc strong {
    color: ${props.$markdownHighlightColor};
  }`
      : ""}

  .markdown-highlight {
    color: ${(props) => props.$markdownHighlightColor};
  }

  .markdown-underline {
    text-decoration: underline;
    text-underline-offset: 4px;
    text-decoration-thickness: 2px;
  }

  .small {
    text-align: center;
    max-width: 1100px;
    margin: 1.35rem auto 0;
    word-wrap: break-word;
  }

  .small p {
    margin: 0 0 0.65rem;
  }

  .small p:last-child {
    margin-bottom: 0;
  }

  .with-connector {
    position: relative;
    margin: 0.5rem 0 1.25rem;
  }

  .with-connector img {
    width: 100%;
    height: auto;
    border-radius: 1.15rem;
  }

  .with-connector::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    z-index: 1;
    width: 4.75rem;
    height: 4.75rem;
    transform: translate(-50%, -50%);
    pointer-events: none;
    background: url(${process.env.REACT_APP_PGFE_IMG_ASSETS_PATH}fit-in/320x320/promotions/dining/players-choice-fall-2026/v4/Arrows-players-choice.png)
      center / contain no-repeat;
  }

  @media (max-width: 575.98px) {
    .with-connector::after {
      width: 3.25rem;
      height: 3.25rem;
    }
  }

  .players-choice-hero .hero_text_container h2 {
    margin: 0;
    line-height: 1.25;
    font-size: clamp(1.5rem, 3vw, 2rem);
    font-weight: 700;
  }

  @media (max-width: 767.98px) {
    .players-choice-hero .hero_text_container > div {
      text-wrap: balance;
    }

    .players-choice-hero .hero_text_container h2 {
      display: inline;
      font-size: clamp(1.35rem, 6.2vw, 1.75rem);
    }

    .players-choice-hero .hero_text_container h2:first-of-type::after {
      content: "\\00a0";
    }
  }

  p {
    margin-bottom: 1rem;
  }
`;

const MainContentSection = styled.div`
  padding-bottom: ${(props) => (props.$compact ? 0 : "1rem")};

  h5 {
    margin-top: 2rem;
    font-weight: bold;
  }

  @media (min-width: 768px) {
    padding: ${(props) =>
      props.$compact ? "1rem 1rem 0" : "2rem 1rem 1rem"};
  }

  container-div {
    background: ${(props) =>
      props.$withBackground
        ? props.$backgroundColor || "rgba(0, 0, 0, 0.5)"
        : "transparent"};
  }

  height: auto;
  width: 100%;
  text-align: left;
`;

export default DynamicDiningPromo;
export { COMPONENT_MAP, buildLegacySections };
